import { useMutation } from '@tanstack/react-query';
import { sendInvoiceEmail, sendInvoiceReminder, type InvoiceEmailOptions } from '@/services/invoice/invoiceEmailService';
import { useToast } from '@/hooks/use-toast';
import type { Invoice } from '@/types/invoice.types';

export function useSendInvoiceEmail() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ invoice, options }: { invoice: Invoice; options: InvoiceEmailOptions }) =>
      sendInvoiceEmail(invoice, options),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Invoice sent',
          description: 'The invoice has been sent successfully.',
        });
      } else {
        toast({
          title: 'Failed to send invoice',
          description: result.error || 'An error occurred while sending the invoice.',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send invoice',
        variant: 'destructive',
      });
    },
  });
}

export function useSendInvoiceReminder() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ invoice, options }: { invoice: Invoice; options: Omit<InvoiceEmailOptions, 'subject' | 'message'> }) =>
      sendInvoiceReminder(invoice, options),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Reminder sent',
          description: 'The payment reminder has been sent successfully.',
        });
      } else {
        toast({
          title: 'Failed to send reminder',
          description: result.error || 'An error occurred while sending the reminder.',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send reminder',
        variant: 'destructive',
      });
    },
  });
}